// Shared helpers for the "open OAuth in a new tab" flow used by both connector
// creation and reconnection. The popup must be opened synchronously inside the
// triggering click (popup blockers reject window.open scheduled after async
// work), then navigated to the authorization URL once the API responds.

const OAUTH_LOADING_HTML = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Authorizing…</title>
<meta name="viewport" content="width=device-width,initial-scale=1" />
<style>
  :root { color-scheme: dark light; }
  html, body { height: 100%; margin: 0; }
  body {
    display: flex; align-items: center; justify-content: center;
    background: #0a0f10; color: #cbd5e1;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
  }
  .panel { display: flex; flex-direction: column; align-items: center; gap: 18px; padding: 24px; }
  .spinner {
    width: 32px; height: 32px; border-radius: 50%;
    border: 3px solid rgba(255,255,255,0.08);
    border-top-color: #10b981;
    animation: spin 0.85s linear infinite;
  }
  .title { font-size: 15px; font-weight: 600; }
  .hint { font-size: 13px; color: #94a3b8; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @media (prefers-color-scheme: light) {
    body { background: #f8fafc; color: #0f172a; }
    .hint { color: #475569; }
    .spinner { border-color: rgba(15,23,42,0.08); border-top-color: #10b981; }
  }
</style>
</head>
<body>
  <div class="panel" role="status" aria-live="polite">
    <div class="spinner" aria-hidden="true"></div>
    <div class="title">Preparing authorization…</div>
    <div class="hint">This window will redirect to the provider in a moment.</div>
  </div>
</body>
</html>`;

const paintOAuthLoadingState = (popup: Window) => {
  try {
    popup.document.open();
    popup.document.write(OAUTH_LOADING_HTML);
    popup.document.close();
  } catch {
    // Some browsers throw if the popup hasn't yet committed `about:blank` —
    // fall through silently; navigation to the auth URL still happens.
  }
};

// Open a blank tab synchronously (inside a user gesture) and paint the loading
// state. Returns the popup handle, or null if the browser blocked it.
// `noopener`/`noreferrer` are intentionally omitted: they make window.open
// return null in some browsers, which would break the later navigation.
export const openOAuthPopup = (): Window | null => {
  const popup = window.open("about:blank", "_blank");
  if (popup) paintOAuthLoadingState(popup);
  return popup;
};

// Settle a previously-opened popup against the API result.
export const settleOAuthPopup = (
  popup: Window | null,
  authorizationUrl: string | undefined,
): { redirected: boolean; blocked: boolean } => {
  if (!popup) {
    return { redirected: false, blocked: true };
  }
  if (authorizationUrl) {
    popup.location.assign(authorizationUrl);
    return { redirected: true, blocked: false };
  }
  // Nothing to navigate to — don't leave a stranded blank tab.
  popup.close();
  return { redirected: false, blocked: false };
};

const OAUTH_POPUP_POLL_INTERVAL_MS = 500;
const OAUTH_POPUP_TIMEOUT_MS = 15 * 60 * 1000;

// OAuth runs on another origin, so the opener cannot inspect the popup while
// the provider is visible. Once the callback sends it back to this app (or the
// user closes it), the connection can be refreshed safely. This keeps the
// first authorization attempt from leaving the list's connection test cached
// in its pre-authentication state.
export const watchOAuthPopupCompletion = (
  popup: Window,
  authorizationUrl: string,
  onComplete: () => void,
): (() => void) => {
  let completed = false;
  let hasReachedProvider = false;

  const finish = () => {
    if (completed) return;
    completed = true;
    window.clearInterval(intervalId);
    window.clearTimeout(timeoutId);
    onComplete();
  };

  const checkPopup = () => {
    if (popup.closed) {
      finish();
      return;
    }

    try {
      const href = popup.location.href;
      if (href === "about:blank") return;

      if (
        hasReachedProvider &&
        href !== authorizationUrl &&
        popup.location.origin === window.location.origin
      ) {
        finish();
      }
    } catch {
      // Reading a cross-origin popup throws while the provider is visible.
      // Keep polling until the callback returns the popup to this app.
      hasReachedProvider = true;
    }
  };

  const intervalId = window.setInterval(
    checkPopup,
    OAUTH_POPUP_POLL_INTERVAL_MS,
  );
  const timeoutId = window.setTimeout(() => {
    completed = true;
    window.clearInterval(intervalId);
  }, OAUTH_POPUP_TIMEOUT_MS);

  // Avoid waiting for the first interval when the popup has already closed.
  checkPopup();

  return () => {
    completed = true;
    window.clearInterval(intervalId);
    window.clearTimeout(timeoutId);
  };
};
